import React from 'react';
import { Download } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';

/**
 * GuestListCSVExport - Exports guest list to CSV format
 * Extracted from legacy Summary Dialog (Phase 3J)
 * 
 * Props:
 * - guestList: Array of guest objects with name, email, bookingId, contactName, roomType, specialRequests, dietary
 * - eventSlug: String for filename
 * - disabled: Boolean to disable the button
 * 
 * CSV Format (matching legacy implementation):
 * - Name: Attendee name from guests[] array
 * - Booking Ref: First 8 characters of booking_id, uppercase
 * - Customer: Booking contact name (customer_name)
 * - Room Type: Room type for the booking
 * - Dietary Requirements: Dietary requirements if provided
 * 
 * Guest Model Rules:
 * - customer_name is the booking contact, NOT automatically an attendee
 * - guests[] contains actual attendees
 * - number_of_guests is the authoritative count
 * - Missing attendee names are NOT invented - only guests[] entries with names are exported
 */
export default function GuestListCSVExport({ guestList, eventSlug, disabled = false }) {
  
  const handleExport = () => {
    if (!guestList || guestList.length === 0) {
      toast.error('No guests to export');
      return;
    }

    // Build CSV with headers matching legacy format
    const headers = ['Name', 'Booking Ref', 'Customer', 'Room Type', 'Dietary Requirements'];
    
    const rows = guestList.map(guest => {
      // Format booking ref: first 8 characters, uppercase (matching legacy)
      const bookingRef = (guest.bookingId || '').slice(0, 8).toUpperCase();
      
      return [
        escapeCSVField(guest.name || ''),
        escapeCSVField(bookingRef),
        escapeCSVField(guest.contactName || ''),
        escapeCSVField(guest.roomType || ''),
        escapeCSVField(guest.dietary || guest.specialRequests || '')
      ];
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `guest-list-${eventSlug || 'event'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success(`Guest list exported (${guestList.length} attendees)`);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={disabled || !guestList || guestList.length === 0}
      data-testid="guest-list-csv-export-btn"
    >
      <Download className="w-4 h-4 mr-2" />
      Export CSV
    </Button>
  );
}

/**
 * Escape special characters in CSV fields
 * - Wrap in quotes if contains comma, quote, or newline
 * - Double any quote characters
 */
function escapeCSVField(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  
  // Check if field needs escaping
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    // Escape double quotes by doubling them
    return `"${str.replace(/"/g, '""')}"`;
  }
  
  return str;
}
